import { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  X,
  Clock,
  CheckCircle2,
  Package,
  Search,
  Utensils,
  Star,
  RotateCcw,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import { api } from '../../lib/api';

interface OrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrackOrder: (orderId: string) => void;
  onRateOrder: (order: { id: string; restaurantId: string; restaurantName?: string; rating?: number }) => void;
}

type OrderFilter = 'all' | 'active' | 'delivered' | 'cancelled';

const normalizeStatus = (status: any) => String(status || 'CONFIRMED').trim().toUpperCase();

const getOrderStatusLabel = (status: any) => {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'OUT_FOR_DELIVERY':
      return 'Out for Delivery';
    case 'DELIVERED':
      return 'Delivered';
    case 'COMPLETED':
      return 'Delivered';
    case 'CANCELLED':
    case 'CANCELED':
      return 'Cancelled';
    case 'PREPARING':
      return 'Preparing';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'READY':
      return 'Ready';
    case 'PENDING':
      return 'Pending';
    default:
      return String(status || '')
        .trim()
        .toLowerCase()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || 'Confirmed';
  }
};

const getOrderTimestamp = (order: any) => {
  const rawTimestamp = order?.timestamp || order?.createdAt || order?.updatedAt;
  const parsedDate = rawTimestamp ? new Date(rawTimestamp) : null;
  return parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : new Date();
};

const statusMatchesFilter = (order: any, status: OrderFilter) => {
  const orderStatus = normalizeStatus(order?.status);

  if (status === 'active') {
    return !['DELIVERED', 'COMPLETED', 'CANCELLED', 'CANCELED'].includes(orderStatus);
  }
  if (status === 'delivered') {
    return ['DELIVERED', 'COMPLETED'].includes(orderStatus);
  }
  if (status === 'cancelled') {
    return ['CANCELLED', 'CANCELED'].includes(orderStatus);
  }

  return true;
};

const mapApiOrderToHistoryOrder = (order: any) => ({
  id: String(order?.id || order?.orderNumber || ''),
  orderNumber: String(order?.orderNumber || order?.id || ''),
  restaurantId: String(order?.restaurantId || order?.restaurant?.id || ''),
  restaurantName: String(order?.restaurant?.name || order?.restaurantName || 'Restaurant'),
  customerId: String(order?.customerId || order?.customer?.id || ''),
  items: Array.isArray(order?.items)
    ? order.items.map((item: any) => ({
        id: String(item?.id || item?.menuItemId || ''),
        menuItemId: String(item?.menuItemId || item?.menuItem?.id || ''),
        name: String(item?.menuItem?.name || item?.name || 'Item'),
        price: Number(item?.price || 0),
        quantity: Number(item?.quantity || 0),
        subtotal: Number(item?.subtotal || 0),
      }))
    : [],
  total: Number(order?.total || 0),
  status: getOrderStatusLabel(order?.status),
  statusCode: normalizeStatus(order?.status),
  timestamp: String(order?.placedAt || order?.createdAt || order?.updatedAt || new Date().toISOString()),
  createdAt: String(order?.createdAt || order?.placedAt || new Date().toISOString()),
  updatedAt: String(order?.updatedAt || order?.createdAt || new Date().toISOString()),
  rating: order?.review?.rating ?? order?.rating,
  review: order?.review || null,
});

const REVIEW_STORAGE_KEY = 'submitted_order_reviews';

const getStoredReviews = () => JSON.parse(localStorage.getItem(REVIEW_STORAGE_KEY) || '{}');

export function OrderHistoryModal({ isOpen, onClose, onTrackOrder, onRateOrder }: OrderHistoryModalProps) {
  const { formatMoney } = useLocale();
  const [orders, setOrders] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<OrderFilter>('all');

  useEffect(() => {
    if (isOpen) {
      void loadOrders();
    }
  }, [isOpen]);

  const loadOrders = async () => {
    try {
      const hasToken = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
      if (!hasToken) {
        throw new Error('guest_no_token');
      }
      const response = await api.orders.getMyOrders({ page: 1, limit: 100 });
      const storedReviews = getStoredReviews();
      const apiOrders = Array.isArray(response?.data?.orders)
        ? response.data.orders.map((order: any) => {
            const mapped = mapApiOrderToHistoryOrder(order);
            const storedReview = storedReviews[mapped.id];
            return storedReview ? { ...mapped, rating: mapped.rating || storedReview.rating } : mapped;
          })
        : [];

      const sortedOrders = apiOrders.sort(
        (a: any, b: any) => getOrderTimestamp(b).getTime() - getOrderTimestamp(a).getTime()
      );
      setOrders(sortedOrders);
    } catch {
      const storedReviews = getStoredReviews();
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      const currentUserId = String(currentUser?.id || '');
      const storedOrders = JSON.parse(localStorage.getItem('orders') || '[]');
      const filteredLocalOrders = currentUserId
        ? storedOrders.filter((order: any) => String(order?.customerId || '') === currentUserId)
        : storedOrders;
      const mergedOrders = filteredLocalOrders.map((order: any) => {
        const storedReview = storedReviews[String(order?.id || '')];
        return storedReview ? { ...order, rating: order?.rating || storedReview.rating } : order;
      });
      setOrders(
        mergedOrders.sort((a: any, b: any) => getOrderTimestamp(b).getTime() - getOrderTimestamp(a).getTime())
      );
    }
  };

  const filteredOrders = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return orders.filter((order) => {
      if (!statusMatchesFilter(order, filterStatus)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const orderId = String(order?.id || '').toLowerCase();
      const restaurantName = String(order?.restaurantName || order?.restaurant?.name || 'Restaurant').toLowerCase();
      const itemMatches = Array.isArray(order?.items)
        ? order.items.some((item: any) => String(item?.name || '').toLowerCase().includes(normalizedQuery))
        : false;

      return orderId.includes(normalizedQuery) || restaurantName.includes(normalizedQuery) || itemMatches;
    });
  }, [filterStatus, orders, searchQuery]);

  const filterTabs = useMemo(
    () => [
      { key: 'all' as const, label: 'All Orders', count: orders.length },
      { key: 'active' as const, label: 'Active', count: orders.filter((order) => statusMatchesFilter(order, 'active')).length },
      { key: 'delivered' as const, label: 'Delivered', count: orders.filter((order) => statusMatchesFilter(order, 'delivered')).length },
      { key: 'cancelled' as const, label: 'Cancelled', count: orders.filter((order) => statusMatchesFilter(order, 'cancelled')).length },
    ],
    [orders]
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DELIVERED':
      case 'COMPLETED':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'OUT_FOR_DELIVERY':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'PREPARING':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'CANCELLED':
      case 'CANCELED':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DELIVERED':
      case 'COMPLETED':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'OUT_FOR_DELIVERY':
        return <Package className="w-4 h-4" />;
      case 'CANCELLED':
      case 'CANCELED':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return 'Recently placed';
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleReorder = (order: any) => {
    alert(`Reorder functionality for order #${order.id}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 text-white sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div>
                <h2 className="text-xl font-bold sm:text-2xl">Order History</h2>
                <p className="mt-1 text-blue-100 text-xs sm:text-sm">{orders.length} total orders</p>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 transition-colors hover:bg-white/20 sm:p-2"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 sm:left-3 sm:h-5 sm:w-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders, restaurants, or items..."
                className="w-full rounded-lg border border-white/20 bg-white/10 py-2 pl-9 pr-3 text-sm text-white placeholder-blue-200 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/30 sm:py-3 sm:pl-10 sm:pr-4"
              />
            </div>

            <div className="mt-3 flex w-full max-w-full touch-pan-x gap-1.5 overflow-x-auto overscroll-x-contain pb-1 [-webkit-overflow-scrolling:touch] sm:mt-4 sm:gap-2 sm:pb-2">
              {filterTabs.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors sm:px-4 sm:py-2 sm:text-sm ${
                    filterStatus === filter.key
                      ? 'bg-white text-blue-600'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Receipt className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h3>
                <p className="text-gray-600">
                  {searchQuery
                    ? 'Try adjusting your search or filters'
                    : 'Your order history will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order, index) => (
                  <div
                    key={`${order.id}-${index}`}
                    className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow sm:p-5"
                  >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                        <Utensils className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="truncate font-bold text-gray-900">
                          {order.restaurantName || order.restaurant?.name || 'Restaurant'}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">Order #{order.orderNumber || order.id}</p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-bold text-gray-900">{formatMoney(Number(order.total || 0))}</p>
                      <p className="text-[11px] text-gray-600 sm:text-xs">{order.items?.length || 0} items</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {order.items?.slice(0, 3).map((item: any, index: number) => (
                        <span
                          key={index}
                          className="text-sm text-gray-700 bg-gray-50 px-3 py-1 rounded-full"
                        >
                          {item.quantity}x {item.name}
                        </span>
                      ))}
                      {order.items?.length > 3 && (
                        <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                          +{order.items.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-4 border-b border-gray-100 pb-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div
                          className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium leading-none sm:px-3 sm:py-1 sm:text-sm ${getStatusColor(
                            String(order.statusCode || normalizeStatus(order?.status))
                          )}`}
                        >
                          {getStatusIcon(String(order.statusCode || normalizeStatus(order?.status)))}
                          {getOrderStatusLabel(order?.status)}
                        </div>
                      </div>
                      <p className="whitespace-nowrap text-xs text-gray-600 sm:text-sm">
                        {formatDate(order.timestamp || order.createdAt || order.updatedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!['DELIVERED', 'COMPLETED', 'CANCELLED', 'CANCELED'].includes(
                      String(order.statusCode || normalizeStatus(order?.status))
                    ) && (
                      <button
                        onClick={() => onTrackOrder(order.id)}
                        className="flex-1 flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-blue-700 sm:px-4 sm:py-2.5 sm:text-sm"
                      >
                        <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="whitespace-nowrap">Track Order</span>
                      </button>
                    )}

                    <button
                      onClick={() => handleReorder(order)}
                      className="flex-1 flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 sm:px-4 sm:py-2.5 sm:text-sm"
                    >
                      <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span className="whitespace-nowrap">Reorder</span>
                    </button>

                  </div>

                  {String(order.statusCode || normalizeStatus(order?.status)) === 'DELIVERED' && !order.rating && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">How was your order?</p>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              className="p-1 hover:scale-110 transition-transform"
                              onClick={() =>
                                onRateOrder({
                                  id: String(order.id),
                                  restaurantId: String(order.restaurantId || order.restaurant?.id || ''),
                                  restaurantName: String(order.restaurantName || order.restaurant?.name || ''),
                                  rating: star,
                                })
                              }
                            >
                              <Star className="w-5 h-5 text-gray-400 hover:text-yellow-400 hover:fill-yellow-400" />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {order.rating && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600">Your rating:</p>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= order.rating
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        </div>

        {filteredOrders.length > 0 && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <p className="text-sm text-gray-600 text-center">
              Showing {filteredOrders.length} of {orders.length} orders
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
